import type { LevelDef, LevelFile } from '../content/levelSchema';
import { MOWER_DEFS, MOWER_IDS, type MowerId } from '../content/mowers';
import { TILE_BY_ID, type TileId } from '../content/tiles';
import { Dir, dx, dy, type Direction } from '../sim/direction';
import { Rng } from '../sim/rng';
import type { CarryOver, Simulation } from '../sim/simulation';
import { loadLevel } from '../content/levelSchema';
import {
  bossAllows,
  COUNTY_FAIR,
  generateArtLawn,
  generateDarkLawn,
  generateGreatMeadow,
  generateMazeLawn,
  generateHerdLawn,
  generateRainChase,
  generateRoboLawn,
  type BossKind,
  generateSoccerPitch,
  type SoccerOptions,
} from './boss';
import { generateLevel, paramsForLevel, type GenParams } from './generator';
import { itemBoxCount, withItemBoxes } from './itemBoxes';
import { solveGreedy } from './solver/greedyPlanner';

/**
 * Runtime-generated modes (docs/GDD.md §7). Pure and deterministic: the caller supplies the date
 * or run seed (wall-clock and randomness live in the game layer).
 */

export interface DailyLawn {
  dateKey: string;
  def: LevelDef;
  /** Everyone plays the daily with the same loaner mower, unlocked or not. */
  loaner: MowerId;
  difficulty: number;
}

/** `YYYY-MM-DD` for a calendar date (month 1–12). */
export function dateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The same lawn for every player on a given date. With `boxes` (the default; rules version 5 on,
 * docs/DAILY_ITEMS.md) it carries item boxes; without, it is the lawn earlier versions played,
 * which the server still checks their runs on.
 */
export function dailyLawn(key: string, { boxes = true }: { boxes?: boolean } = {}): DailyLawn {
  const seed = `daily:${key}`;
  const rng = Rng.fromSeed(seed);
  const loaner = rng.pick(MOWER_IDS);
  const difficulty = rng.intBetween(8, 16);
  const params = {
    ...paramsForLevel(difficulty, { seed, id: `daily-${key}` }),
    mowerRule: { kind: 'only' as const, mowers: [loaner] },
    required: [loaner],
  };
  const made = generateLevel(params).def;
  const def = boxes ? withItemBoxes(made, seed, itemBoxCount(difficulty)) : made;
  return { dateKey: key, def: { ...def, name: `Daily: ${def.name}` }, loaner, difficulty };
}

/** How big a live race's lawn is (docs/LIVE.md): the inviter picks. */
export const RACE_SIZES = ['quick', 'normal', 'long'] as const;
export type RaceSize = (typeof RACE_SIZES)[number];

/** Each size's difficulty range: QUICK about a minute, NORMAL a Daily, LONG the biggest. */
const RACE_DIFFICULTY: Record<RaceSize, readonly [number, number]> = {
  quick: [4, 7],
  normal: [8, 16],
  long: [17, 20],
};
/** How many coins each size's lawn carries (each goes to whichever racer reaches it first). */
export const RACE_COINS: Record<RaceSize, readonly [number, number]> = {
  quick: [3, 4],
  normal: [4, 6],
  long: [6, 8],
};

export interface RaceLawn {
  seed: string;
  size: RaceSize;
  def: LevelDef;
  /** Both racers mow with this loaner mower, unlocked or not, with no upgrades or tools. */
  loaner: MowerId;
  difficulty: number;
}

/**
 * A live race's lawn (docs/LIVE.md), the same for both racers: made from the room's seed like the
 * Daily, with the loaner picked from the seed. Its only pickups are coins (a few, by size), gas and
 * repairs: no rare finds or treasure in a race.
 */
export function raceLawn(seed: string, size: RaceSize): RaceLawn {
  const rng = Rng.fromSeed(`race:${size}:${seed}`);
  const loaner = rng.pick(MOWER_IDS);
  const [lo, hi] = RACE_DIFFICULTY[size];
  const difficulty = rng.intBetween(lo, hi);
  const [fewest, most] = RACE_COINS[size];
  const coins = rng.intBetween(fewest, most);
  const base = paramsForLevel(difficulty, { seed: `race:${seed}`, id: `race-${size}` });
  const params = {
    ...base,
    coins: Math.max(base.coins, most),
    rare: false,
    mowerRule: { kind: 'only' as const, mowers: [loaner] },
    required: [loaner],
  };
  const { def } = generateLevel(params);
  let kept = 0;
  const pickups = (def.sim.pickups ?? []).filter(
    (p) => p.kind === 'gas' || p.kind === 'repair' || (p.kind === 'coin' && kept++ < coins),
  );
  return {
    seed,
    size,
    def: { ...def, name: `Race: ${def.name}`, sim: { ...def.sim, pickups } },
    loaner,
    difficulty,
  };
}

/**
 * Head to head's lawns by size (docs/HEAD_TO_HEAD.md): bigger than a race's, since two mowers share
 * one, with an even height so it splits into two halves; and the difficulty its half is made at.
 */
const DUEL_SIZE: Record<RaceSize, { w: number; h: number; difficulty: number }> = {
  quick: { w: 8, h: 10, difficulty: 6 },
  normal: { w: 10, h: 14, difficulty: 12 },
  long: { w: 12, h: 18, difficulty: 18 },
};

/** Mowers a match can lend: one tile wide, without side reels (the sim's rule for matches). */
export const DUEL_MOWERS: readonly MowerId[] = MOWER_IDS.filter(
  (id) => (MOWER_DEFS[id].cutWidth ?? 1) === 1 && !MOWER_DEFS[id].wings,
);

export interface DuelLawn {
  seed: string;
  size: RaceSize;
  /** The lawn, its `sim.seats` the two corners. */
  def: LevelDef;
  /** Both players mow with this loaner, with no upgrades or tools. */
  loaner: MowerId;
}

/** Tries before a seed gives up (a half is made again until the loaner can clear the whole). */
const DUEL_TRIES = 40;

/**
 * A head-to-head lawn (docs/HEAD_TO_HEAD.md), the same for both players: a generated half (only
 * obstacles, tall grass, flower beds, coins, gas and repairs) turned 180 degrees onto the other
 * half, so each start corner sees the same lawn. Seat 0 starts top left facing east, seat 1 bottom
 * right facing west. The loaner, picked from the seed, must clear the whole lawn alone.
 */
export function duelLawn(seed: string, size: RaceSize): DuelLawn {
  const rng = Rng.fromSeed(`duel:${size}:${seed}`);
  const loaner = rng.pick(DUEL_MOWERS);
  const { w, h, difficulty } = DUEL_SIZE[size];
  const [, most] = RACE_COINS[size];
  for (let attempt = 1; attempt <= DUEL_TRIES; attempt++) {
    const base = paramsForLevel(difficulty, {
      seed: `duel:${seed}:${attempt}`,
      id: `duel-${size}`,
    });
    // Everything not yet fair to two mowers off (and the frills a match does without).
    const params: GenParams = {
      level: base.level,
      seed: base.seed,
      id: base.id,
      biome: base.biome,
      width: w,
      height: h,
      budget: base.budget,
      terrainPatches: base.terrainPatches,
      critters: 0,
      coins: most,
      rare: false,
      repair: base.repair,
      mowerRule: { kind: 'only', mowers: [loaner] },
      required: [loaner],
      ...(base.overgrown !== undefined ? { overgrown: base.overgrown } : {}),
    };
    const { def } = generateLevel(params);
    const art = def.art.map((tile, i) => {
      const x = i % w;
      const y = Math.floor(i / w);
      return y < h / 2 ? tile : def.art[(h - 1 - y) * w + (w - 1 - x)]!;
    });
    // Each start and the tile ahead of it open grass.
    for (const i of [0, 1, w * h - 1, w * h - 2]) art[i] = 'grass';
    const turn = (p: { x: number; y: number }) => ({ x: w - 1 - p.x, y: h - 1 - p.y });
    const kept = (def.sim.pickups ?? []).filter(
      (p) =>
        p.y < h / 2 &&
        (p.kind === 'coin' || p.kind === 'gas' || p.kind === 'repair') &&
        !(p.y === 0 && p.x <= 1),
    );
    let coins = 0;
    const half = kept.filter((p) => p.kind !== 'coin' || coins++ < Math.ceil(most / 2));
    const pickups = [...half, ...half.map((p) => ({ ...p, ...turn(p) }))];
    const rows = Array.from({ length: h }, (_, y) =>
      art
        .slice(y * w, (y + 1) * w)
        .map((id, x) => (x === 0 && y === 0 ? 'S' : TILE_BY_ID[id].char)),
    ).map((r) => r.join(''));
    const file: LevelFile = {
      id: `duel-${size}`,
      name: `Head to head: ${def.name}`,
      seed: `duel:${seed}`,
      startDir: 'E',
      mowerRule: { kind: 'only', mowers: [loaner] },
      rows,
      pickups,
      critters: [],
    };
    const level = loadLevel(file);
    const sim = { ...level.sim, seats: [level.sim.start, { x: w - 1, y: h - 1, dir: Dir.W }] };
    const route = solveGreedy(level.sim, MOWER_DEFS[loaner]);
    if (!route.won || route.crashes > 0) continue;
    return { seed, size, loaner, def: { ...level, sim } };
  }
  throw new Error(`duel-${size}:${seed}: no fair lawn after ${DUEL_TRIES} tries`);
}

/**
 * A party's head-to-head lawn by size (docs/PARTY.md): square (so it turns a quarter round onto
 * itself), bigger than a match of two's for three or four mowers.
 */
export const PARTY_DUEL_SIZE: Record<RaceSize, { w: number; difficulty: number }> = {
  quick: { w: 12, difficulty: 8 },
  normal: { w: 14, difficulty: 14 },
  long: { w: 16, difficulty: 18 },
};

/** A tile's place turned a quarter round (clockwise) on a w x w lawn. */
function quarterTurn(w: number, p: { x: number; y: number }): { x: number; y: number } {
  return { x: w - 1 - p.y, y: p.x };
}

/**
 * The corners of a w x w lawn going round clockwise from the top left, each start facing along
 * the edge into the lawn (east, south, west, north): a party's seats (docs/PARTY.md).
 */
export function partyCorners(w: number): { x: number; y: number; dir: Direction }[] {
  return [
    { x: 0, y: 0, dir: Dir.E },
    { x: w - 1, y: 0, dir: Dir.S },
    { x: w - 1, y: w - 1, dir: Dir.W },
    { x: 0, y: w - 1, dir: Dir.N },
  ];
}

/**
 * A party's head-to-head lawn (docs/PARTY.md), the same seen from every corner: a generated lawn
 * whose every tile takes the art of the first of its four quarter turns (row by row), so it is the
 * same turned a quarter round; coins, gas and repairs likewise in fours. Seats in the corners
 * going round from the top left, the first `players` of them. The loaner, picked from the seed,
 * must clear the whole lawn alone.
 */
export function partyDuelLawn(seed: string, size: RaceSize, players: number): DuelLawn {
  const rng = Rng.fromSeed(`party:${size}:${seed}`);
  const loaner = rng.pick(DUEL_MOWERS);
  const { w, difficulty } = PARTY_DUEL_SIZE[size];
  const [, most] = RACE_COINS[size];
  const orbit = (p: { x: number; y: number }) => {
    const out = [p];
    for (let k = 1; k < 4; k++) out.push(quarterTurn(w, out[k - 1]!));
    return out;
  };
  const first = (p: { x: number; y: number }) =>
    orbit(p).reduce((a, b) => (b.y * w + b.x < a.y * w + a.x ? b : a));
  const corners = partyCorners(w);
  for (let attempt = 1; attempt <= DUEL_TRIES; attempt++) {
    const base = paramsForLevel(difficulty, {
      seed: `party:${seed}:${attempt}`,
      id: `party-${size}`,
    });
    const params: GenParams = {
      level: base.level,
      seed: base.seed,
      id: base.id,
      biome: base.biome,
      width: w,
      height: w,
      budget: base.budget,
      terrainPatches: base.terrainPatches,
      critters: 0,
      coins: most * 2,
      rare: false,
      repair: base.repair,
      mowerRule: { kind: 'only', mowers: [loaner] },
      required: [loaner],
      ...(base.overgrown !== undefined ? { overgrown: base.overgrown } : {}),
    };
    const { def } = generateLevel(params);
    const art = def.art.map((_, i) => {
      const f = first({ x: i % w, y: Math.floor(i / w) });
      return def.art[f.y * w + f.x]!;
    });
    // Each corner and the tile ahead of it open grass.
    for (const c of corners) {
      art[c.y * w + c.x] = 'grass';
      art[(c.y + dy(c.dir)) * w + c.x + dx(c.dir)] = 'grass';
    }
    const kept = (def.sim.pickups ?? []).filter((p) => {
      const f = first(p);
      return (
        f.x === p.x &&
        f.y === p.y &&
        (p.kind === 'coin' || p.kind === 'gas' || p.kind === 'repair') &&
        art[p.y * w + p.x] === 'grass' &&
        orbit(p).every((q) => !corners.some((c) => c.x === q.x && c.y === q.y))
      );
    });
    let coins = 0;
    const quarter = kept.filter((p) => p.kind !== 'coin' || coins++ < Math.ceil(most / 2));
    const pickups = quarter.flatMap((p) => {
      const seen = new Set<number>();
      return orbit(p)
        .filter((q) => !seen.has(q.y * w + q.x) && seen.add(q.y * w + q.x))
        .map((q) => ({ ...p, ...q }));
    });
    const start = corners[0]!;
    const rows = Array.from({ length: w }, (_, y) =>
      art
        .slice(y * w, (y + 1) * w)
        .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
        .join(''),
    );
    const file: LevelFile = {
      id: `party-${size}`,
      name: `Head to head: ${def.name}`,
      seed: `party:${seed}`,
      startDir: 'E',
      mowerRule: { kind: 'only', mowers: [loaner] },
      rows,
      pickups,
      critters: [],
    };
    const level = loadLevel(file);
    const route = solveGreedy(level.sim, MOWER_DEFS[loaner]);
    if (!route.won || route.crashes > 0) continue;
    const sim = { ...level.sim, seats: corners.slice(0, players) };
    return { seed, size, loaner, def: { ...level, sim } };
  }
  throw new Error(`party-${size}:${seed}: no fair lawn after ${DUEL_TRIES} tries`);
}

/**
 * Soccer matches by size (docs/SOCCER_MATCH.md): the pitch (odd sizes, so it is the same turned
 * round), the goals that win at once, the whistle, and pairs of training cones.
 */
export const SOCCER_MATCH: Record<
  RaceSize,
  { w: number; h: number; target: number; seconds: number; cones: number }
> = {
  quick: { w: 11, h: 15, target: 2, seconds: 45, cones: 1 },
  normal: { w: 13, h: 17, target: 3, seconds: 60, cones: 2 },
  long: { w: 15, h: 19, target: 4, seconds: 70, cones: 3 },
};

/**
 * Mowers a soccer match lends: one tile wide (the match rule), more than one heart (a fence is
 * never far), and gas for the longest match.
 */
export const SOCCER_MOWERS: readonly MowerId[] = ['selfPropelled', 'zeroTurn'];

/** How many tiles wide each goal mouth is on a match pitch (odd, so it sits in the middle). */
export const SOCCER_MATCH_MOUTH = 3;

/**
 * A soccer match's pitch (docs/SOCCER_MATCH.md), the same for both players: open grass with corner
 * flags and training cones in pairs turned 180 degrees, a goal mouth in the middle of each end, and
 * the ball on the center spot. Seat 0 starts below the ball facing up (attacking the top goal),
 * seat 1 above it facing down, each the same distance from it.
 */
export function soccerMatchLawn(seed: string, size: RaceSize): DuelLawn {
  return soccerPitch(seed, size, SOCCER_MATCH[size], 2);
}

/**
 * Soccer two against two by size (docs/PARTY.md): a bigger pitch for four mowers, the goals that
 * win, the whistle (inside the Zero-Turn's gas), and pairs of training cones.
 */
export const PARTY_SOCCER: Record<
  RaceSize,
  { w: number; h: number; target: number; seconds: number; cones: number }
> = {
  quick: { w: 13, h: 17, target: 2, seconds: 45, cones: 1 },
  normal: { w: 15, h: 19, target: 3, seconds: 60, cones: 2 },
  long: { w: 17, h: 21, target: 4, seconds: 70, cones: 3 },
};

/**
 * Soccer two against two (docs/PARTY.md): the pitch for four, seats 0 and 2 side by side below the
 * ball attacking the top goal, 1 and 3 the same turned round (teams by seat: even against odd).
 */
export function partySoccerLawn(seed: string, size: RaceSize): DuelLawn {
  return soccerPitch(`party:${seed}`, size, PARTY_SOCCER[size], 4);
}

/** A soccer pitch (see {@link soccerMatchLawn}) for two, or for two teams of two. */
function soccerPitch(
  seed: string,
  size: RaceSize,
  spec: { w: number; h: number; target: number; seconds: number; cones: number },
  players: 2 | 4,
): DuelLawn {
  const rng = Rng.fromSeed(`soccer:${size}:${seed}`);
  const loaner = rng.pick(SOCCER_MOWERS);
  const { w, h, target, seconds, cones } = spec;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const art = new Array<TileId>(w * h).fill('grass');
  for (const [x, y] of [
    [0, 0],
    [w - 1, 0],
    [0, h - 1],
    [w - 1, h - 1],
  ] as const) {
    art[y * w + x] = 'cornerFlag';
  }
  // Cones in the top half, each with its partner turned round into the bottom half: off the middle
  // lane, clear of the ends and of each other.
  let placed = 0;
  for (let tries = 0; placed < cones && tries < 200; tries++) {
    const x = rng.intBetween(1, w - 2);
    const y = rng.intBetween(3, cy - 2);
    if (Math.abs(x - cx) < 2) continue;
    const near = [-1, 0, 1].some((ox) =>
      [-1, 0, 1].some((oy) => art[(y + oy) * w + x + ox] === 'cone'),
    );
    if (near) continue;
    art[y * w + x] = 'cone';
    art[(h - 1 - y) * w + (w - 1 - x)] = 'cone';
    placed++;
  }
  // Two teams of two start side by side, each pair turned round from the other.
  const start = players === 4 ? { x: cx - 2, y: h - 3 } : { x: cx, y: h - 3 };
  const rows = Array.from({ length: h }, (_, y) =>
    art
      .slice(y * w, (y + 1) * w)
      .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
      .join(''),
  );
  const file: LevelFile = {
    id: players === 4 ? `party-soccer-${size}` : `soccer-${size}`,
    name: 'Mower Soccer',
    seed: `soccer:${seed}`,
    biome: 'stadium',
    startDir: 'N',
    mowerRule: { kind: 'only', mowers: [loaner] },
    rows,
    pickups: [],
    critters: [],
    soccer: {
      target,
      halfTiles: 100_000,
      mouth: SOCCER_MATCH_MOUTH,
      stars: { goals: target, mowed: 1 },
    },
  };
  const level = loadLevel(file);
  const sim = {
    ...level.sim,
    soccer: { ...level.sim.soccer!, matchTicks: seconds * 60 },
    seats:
      players === 4
        ? [
            level.sim.start,
            { x: cx + 2, y: 2, dir: Dir.S },
            { x: cx + 2, y: h - 3, dir: Dir.N },
            { x: cx - 2, y: 2, dir: Dir.S },
          ]
        : [level.sim.start, { x: cx, y: 2, dir: Dir.S }],
  };
  return { seed, size, loaner, def: { ...level, sim } };
}

/**
 * Sheep round-up matches by size (docs/ROUNDUP_MATCH.md): the paddock (odd sizes, so it is the
 * same turned round), the sheep (odd, so there is always a winner once all are in), the sheep penned
 * that win at once (more than half), the whistle, and pairs of hay bales.
 */
export const ROUNDUP_MATCH: Record<
  RaceSize,
  { w: number; h: number; sheep: number; target: number; seconds: number; bales: number }
> = {
  quick: { w: 11, h: 15, sheep: 5, target: 3, seconds: 50, bales: 1 },
  normal: { w: 13, h: 17, sheep: 7, target: 4, seconds: 60, bales: 2 },
  long: { w: 15, h: 19, sheep: 9, target: 5, seconds: 70, bales: 2 },
};

/**
 * Mowers a round-up lends: one tile wide (the match rule, and the only width that nudges a sheep),
 * more than one heart (a pen fence is never far), and gas for the longest match.
 */
export const ROUNDUP_MOWERS: readonly MowerId[] = ['selfPropelled', 'zeroTurn'];

/** A round-up pen: this many tiles wide and deep, in the middle of each end row. */
export const ROUNDUP_PEN = { width: 3, depth: 2 } as const;

/**
 * A sheep round-up's paddock (docs/ROUNDUP_MATCH.md), the same for both players: open grass with a
 * fenced pen in the middle of each end (its gate in front), a sheep on the center spot and the
 * rest in pairs turned 180 degrees, and hay bales in pairs too. Each seat's pen is at its own end:
 * seat 0 starts at the bottom, left of the middle, facing up; seat 1 the same from the top.
 */
export function roundupMatchLawn(seed: string, size: RaceSize): DuelLawn {
  const rng = Rng.fromSeed(`roundup:${size}:${seed}`);
  const loaner = rng.pick(ROUNDUP_MOWERS);
  const { w, h, sheep: count, target, seconds, bales } = ROUNDUP_MATCH[size];
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const art = new Array<TileId>(w * h).fill('grass');
  const turn = (p: { x: number; y: number }) => ({ x: w - 1 - p.x, y: h - 1 - p.y });
  const half = (ROUNDUP_PEN.width - 1) / 2;
  for (let y = 0; y < ROUNDUP_PEN.depth; y++) {
    for (let x = cx - half; x <= cx + half; x++) {
      art[y * w + x] = 'pen';
      art[(h - 1 - y) * w + x] = 'pen';
    }
  }
  const gates = [
    { x: cx, y: h - 1 - ROUNDUP_PEN.depth },
    { x: cx, y: ROUNDUP_PEN.depth },
  ];
  for (const g of gates) art[g.y * w + g.x] = 'penGate';
  const starts = [
    { x: cx - 2, y: h - 4, dir: Dir.N },
    { x: cx + 2, y: 3, dir: Dir.S },
  ];
  const near = (a: { x: number; y: number }, b: { x: number; y: number }, r: number) =>
    Math.abs(a.x - b.x) + Math.abs(a.y - b.y) < r;
  // In the top half, each with its partner turned round into the bottom half: well away from the
  // gates (no sheep is a free point) and off the starts.
  const spot = (y0: number) => {
    const p = { x: rng.intBetween(1, w - 2), y: rng.intBetween(y0, cy - 1) };
    const clear =
      art[p.y * w + p.x] === 'grass' &&
      gates.every((g) => !near(p, g, 5)) &&
      starts.every((s) => !near(p, s, 3) && !near(turn(p), s, 3));
    return clear ? p : null;
  };
  const sheep = [{ x: cx, y: cy }];
  for (let tries = 0; sheep.length < count && tries < 400; tries++) {
    const p = spot(ROUNDUP_PEN.depth + 1);
    if (!p || sheep.some((s) => near(p, s, 3) || near(turn(p), s, 3)) || near(p, turn(p), 3)) {
      continue;
    }
    sheep.push(p, turn(p));
  }
  let placed = 0;
  for (let tries = 0; placed < bales && tries < 400; tries++) {
    const p = spot(ROUNDUP_PEN.depth + 2);
    if (!p || Math.abs(p.x - cx) <= 1 || p.y > cy - 2) continue;
    const touches = [-1, 0, 1].some((ox) =>
      [-1, 0, 1].some((oy) => art[(p.y + oy) * w + p.x + ox] !== 'grass'),
    );
    if (touches || sheep.some((s) => near(p, s, 2) || near(turn(p), s, 2))) continue;
    art[p.y * w + p.x] = 'hayBale';
    const q = turn(p);
    art[q.y * w + q.x] = 'hayBale';
    placed++;
  }
  if (sheep.length < count) throw new Error(`roundup-${size}:${seed}: no room for the sheep`);
  const start = starts[0]!;
  const rows = Array.from({ length: h }, (_, y) =>
    art
      .slice(y * w, (y + 1) * w)
      .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
      .join(''),
  );
  const file: LevelFile = {
    id: `roundup-${size}`,
    name: 'Sheep Round-Up',
    seed: `roundup:${seed}`,
    biome: 'farm',
    startDir: 'N',
    mowerRule: { kind: 'only', mowers: [loaner] },
    rows,
    pickups: [],
    critters: sheep.map((s) => ({ kind: 'sheep' as const, x: s.x, y: s.y })),
  };
  const level = loadLevel(file);
  // Each seat's pen: the pen tiles at its own end, behind its gate.
  const pens = gates.map((gate) => ({
    gate,
    pen: level.art.flatMap((t, i) => {
      const p = { x: i % w, y: Math.floor(i / w) };
      return t === 'pen' && p.y < cy === gate.y < cy ? [p] : [];
    }),
  }));
  const sim = {
    ...level.sim,
    roundup: { pens, target, matchTicks: seconds * 60 },
    seats: [level.sim.start, starts[1]!],
  };
  return { seed, size, loaner, def: { ...level, sim } };
}

/**
 * Hedge maze matches by size (docs/MAZE_MATCH.md): the maze in cells (odd, so there is a true
 * middle; the lawn is twice that less one), the whistle, and the shortest way to the middle a maze
 * must have (tiles; a maze whose way is shorter is made again). Inside the Zero-Turn's gas (260
 * tiles, about 73 seconds).
 */
export const MAZE_MATCH: Record<RaceSize, { cells: number; seconds: number; way: number }> = {
  quick: { cells: 7, seconds: 40, way: 26 },
  normal: { cells: 9, seconds: 50, way: 36 },
  long: { cells: 11, seconds: 60, way: 46 },
};

/** Mowers a maze match lends: one tile wide, more than one heart, gas for the longest match. */
export const MAZE_MOWERS: readonly MowerId[] = ['selfPropelled', 'zeroTurn'];

/** Mazes tried before a seed settles for its longest. */
const MAZE_TRIES = 40;

/**
 * A hedge maze match's maze (docs/MAZE_MATCH.md), the same for both players: one-tile paths
 * between tall hedges, made on half the cells and turned halfway round onto the other half (so
 * both seats' best routes are exactly as long), the halves meeting only through the middle, where
 * a little paved plaza is the finish. Braided, so there are no dead ends. Seat 0 starts in the bottom-left corner, seat 1
 * in the top-right, each facing a way out.
 */
export function mazeMatchLawn(seed: string, size: RaceSize): DuelLawn {
  const rng = Rng.fromSeed(`maze:${size}:${seed}`);
  const loaner = rng.pick(MAZE_MOWERS);
  const { cells: n, seconds, way } = MAZE_MATCH[size];
  const w = n * 2 - 1;
  const c = n - 1;
  let best: { art: TileId[]; length: number } | null = null;
  for (let attempt = 0; attempt < MAZE_TRIES && (best?.length ?? 0) < way; attempt++) {
    const art = makeMaze(rng, n);
    const length = mazeWay(art, w, { x: 0, y: w - 1 }, { x: c, y: c });
    if (!best || length > best.length) best = { art, length };
  }
  const art = best!.art;
  // The plaza: the middle tile and the open way into it from each side, paved. Each seat reaches
  // its own side's paving first, so they never meet head on at the finish.
  const plaza = [
    { x: c, y: c },
    ...[
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]
      .map(([ox, oy]) => ({ x: c + ox!, y: c + oy! }))
      .filter((p) => art[p.y * w + p.x] === 'grass'),
  ];
  for (const p of plaza) art[p.y * w + p.x] = 'path';
  // Each seat faces a way out of its corner (the same one, turned round).
  const up = art[(w - 2) * w] === 'grass';
  const start = { x: 0, y: w - 1, dir: up ? Dir.N : Dir.E };
  const other = { x: w - 1, y: 0, dir: up ? Dir.S : Dir.W };
  const rows = Array.from({ length: w }, (_, y) =>
    art
      .slice(y * w, (y + 1) * w)
      .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
      .join(''),
  );
  const file: LevelFile = {
    id: `maze-${size}`,
    name: 'Hedge Maze',
    seed: `maze:${seed}`,
    biome: 'estate',
    startDir: up ? 'N' : 'E',
    mowerRule: { kind: 'only', mowers: [loaner] },
    rows,
    pickups: [],
    critters: [],
  };
  const level = loadLevel(file);
  const sim = {
    ...level.sim,
    maze: { plaza, matchTicks: seconds * 60 },
    seats: [level.sim.start, other],
  };
  return { seed, size, loaner, def: { ...level, sim } };
}

/**
 * A party's hedge maze by size (docs/PARTY.md): the maze in cells (odd), the whistle, and the
 * shortest way to the middle it must have; bigger than a match of two's, for four corners.
 */
export const PARTY_MAZE: Record<RaceSize, { cells: number; seconds: number; way: number }> = {
  quick: { cells: 9, seconds: 40, way: 28 },
  normal: { cells: 11, seconds: 50, way: 38 },
  long: { cells: 13, seconds: 60, way: 46 },
};

/**
 * A party's hedge maze (docs/PARTY.md), the same seen from every corner: a depth-first maze on one
 * quarter of the cells (the block beside the middle, top left) turned a quarter round onto the
 * other three, the four arms meeting only in the middle, braided, with the paved plaza on all four
 * sides of the middle. Seats in the corners going round from the top left (the first `players`).
 */
export function partyMazeLawn(seed: string, size: RaceSize, players: number): DuelLawn {
  const rng = Rng.fromSeed(`partyMaze:${size}:${seed}`);
  const loaner = rng.pick(MAZE_MOWERS);
  const { cells: n, seconds, way } = PARTY_MAZE[size];
  const w = n * 2 - 1;
  const c = n - 1;
  const corners = partyCorners(w);
  let best: { art: TileId[]; length: number } | null = null;
  for (let attempt = 0; attempt < MAZE_TRIES && (best?.length ?? 0) < way; attempt++) {
    const art = makePartyMaze(rng, n);
    const length = mazeWay(art, w, corners[0]!, { x: c, y: c });
    if (!best || length > best.length) best = { art, length };
  }
  const art = best!.art;
  const plaza = [
    { x: c, y: c },
    ...[
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]
      .map(([ox, oy]) => ({ x: c + ox!, y: c + oy! }))
      .filter((q) => art[q.y * w + q.x] === 'grass'),
  ];
  for (const q of plaza) art[q.y * w + q.x] = 'path';
  const start = corners[0]!;
  const rows = Array.from({ length: w }, (_, y) =>
    art
      .slice(y * w, (y + 1) * w)
      .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
      .join(''),
  );
  const file: LevelFile = {
    id: `party-maze-${size}`,
    name: 'Hedge Maze',
    seed: `partyMaze:${seed}`,
    biome: 'estate',
    startDir: 'E',
    mowerRule: { kind: 'only', mowers: [loaner] },
    rows,
    pickups: [],
    critters: [],
  };
  const level = loadLevel(file);
  const sim = {
    ...level.sim,
    maze: { plaza, matchTicks: seconds * 60 },
    seats: corners.slice(0, players),
  };
  return { seed, size, loaner, def: { ...level, sim } };
}

/**
 * An n x n cell maze the same turned a quarter round (see {@link partyMazeLawn}): hedges, and
 * grass paths.
 */
function makePartyMaze(rng: Rng, n: number): TileId[] {
  const w = n * 2 - 1;
  const at = (x: number, y: number) => y * w + x;
  const art = new Array<TileId>(w * w).fill('mazeHedge');
  for (let cy = 0; cy < n; cy++) for (let cx = 0; cx < n; cx++) art[at(cx * 2, cy * 2)] = 'grass';
  // Opens the wall between two neighboring cells, and the same wall in each quarter turn.
  const open = (ax: number, ay: number, bx: number, by: number) => {
    let q = { x: ax + bx, y: ay + by };
    for (let k = 0; k < 4; k++) {
      art[at(q.x, q.y)] = 'grass';
      q = quarterTurn(w, q);
    }
  };
  const ways = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;
  const inside = (cx: number, cy: number) => cx >= 0 && cy >= 0 && cx < n && cy < n;
  const m = (n - 1) / 2;
  // A depth-first maze on the block beside the middle (columns 0 to m, rows above it): its quarter
  // turns are the other three blocks round the middle.
  const quarter = (cx: number, cy: number) => cx <= m && cy < m;
  const seen = new Set<number>([0]);
  const stack: [number, number][] = [[0, 0]];
  while (stack.length > 0) {
    const [cx, cy] = stack[stack.length - 1]!;
    const next = rng
      .shuffle(ways.map(([ox, oy]) => [cx + ox, cy + oy] as [number, number]))
      .find(([nx, ny]) => inside(nx, ny) && quarter(nx, ny) && !seen.has(ny * n + nx));
    if (!next) {
      stack.pop();
      continue;
    }
    seen.add(next[1] * n + next[0]);
    open(cx, cy, next[0], next[1]);
    stack.push(next);
  }
  // The middle joins the four arms: up into this block (and so, turned round, every side).
  open(m, m, m, m - 1);
  // Braided, as for two: each dead end into the neighbor nearest it along the paths.
  const linked = (ax: number, ay: number, bx: number, by: number) =>
    art[at(ax + bx, ay + by)] === 'grass';
  const exits = (cx: number, cy: number) =>
    ways.filter(([ox, oy]) => inside(cx + ox, cy + oy) && linked(cx, cy, cx + ox, cy + oy));
  const along = (fx: number, fy: number): Map<number, number> => {
    const d = new Map<number, number>([[fy * n + fx, 0]]);
    const queue: [number, number][] = [[fx, fy]];
    for (let q = 0; q < queue.length; q++) {
      const [x, y] = queue[q]!;
      for (const [ox, oy] of exits(x, y)) {
        const k = (y + oy) * n + x + ox;
        if (d.has(k)) continue;
        d.set(k, d.get(y * n + x)! + 1);
        queue.push([x + ox, y + oy]);
      }
    }
    return d;
  };
  for (let cy = 0; cy < n; cy++) {
    for (let cx = 0; cx < n; cx++) {
      if (exits(cx, cy).length > 1) continue;
      const closed = ways.filter(
        ([ox, oy]) => inside(cx + ox, cy + oy) && !linked(cx, cy, cx + ox, cy + oy),
      );
      const d = along(cx, cy);
      const far = (o: readonly [number, number]) => d.get((cy + o[1]) * n + cx + o[0]) ?? 0;
      const nearest = Math.min(...closed.map(far));
      const [ox, oy] = rng.pick(closed.filter((o) => far(o) === nearest));
      open(cx, cy, cx + ox, cy + oy);
    }
  }
  return art;
}

/** An n x n cell maze (see {@link mazeMatchLawn}): hedges, and grass paths. */
function makeMaze(rng: Rng, n: number): TileId[] {
  const w = n * 2 - 1;
  const at = (x: number, y: number) => y * w + x;
  const art = new Array<TileId>(w * w).fill('mazeHedge');
  for (let cy = 0; cy < n; cy++) for (let cx = 0; cx < n; cx++) art[at(cx * 2, cy * 2)] = 'grass';
  // Opens the wall between two neighboring cells, and the same wall turned halfway round.
  const open = (ax: number, ay: number, bx: number, by: number) => {
    art[at(ax + bx, ay + by)] = 'grass';
    art[at(w - 1 - ax - bx, w - 1 - ay - by)] = 'grass';
  };
  const ways = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;
  const inside = (cx: number, cy: number) => cx >= 0 && cy >= 0 && cx < n && cy < n;
  const mid = (n * n - 1) / 2;
  // A depth-first maze on the cells before the middle one (row by row); the rest is its twin.
  const half = (cx: number, cy: number) => cy * n + cx < mid;
  const seen = new Set<number>([n - 1]);
  const stack: [number, number][] = [[n - 1, 0]];
  while (stack.length > 0) {
    const [cx, cy] = stack[stack.length - 1]!;
    const next = rng
      .shuffle(ways.map(([ox, oy]) => [cx + ox, cy + oy] as [number, number]))
      .find(([nx, ny]) => inside(nx, ny) && half(nx, ny) && !seen.has(ny * n + nx));
    if (!next) {
      stack.pop();
      continue;
    }
    seen.add(next[1] * n + next[0]);
    open(cx, cy, next[0], next[1]);
    stack.push(next);
  }
  // The middle cell joins the halves: to its left (and so, turned round, to its right).
  const m = (n - 1) / 2;
  open(m, m, m - 1, m);
  // Braided: a dead end gets a second way out (most mowers cannot turn round in a path), into
  // the neighbor nearest it along the paths, so the loop is small and opens no long shortcut.
  const linked = (ax: number, ay: number, bx: number, by: number) =>
    art[at(ax + bx, ay + by)] === 'grass';
  const exits = (cx: number, cy: number) =>
    ways.filter(([ox, oy]) => inside(cx + ox, cy + oy) && linked(cx, cy, cx + ox, cy + oy));
  const along = (fx: number, fy: number): Map<number, number> => {
    const d = new Map<number, number>([[fy * n + fx, 0]]);
    const queue: [number, number][] = [[fx, fy]];
    for (let q = 0; q < queue.length; q++) {
      const [x, y] = queue[q]!;
      for (const [ox, oy] of exits(x, y)) {
        const k = (y + oy) * n + x + ox;
        if (d.has(k)) continue;
        d.set(k, d.get(y * n + x)! + 1);
        queue.push([x + ox, y + oy]);
      }
    }
    return d;
  };
  for (let cy = 0; cy < n; cy++) {
    for (let cx = 0; cx < n; cx++) {
      if (exits(cx, cy).length > 1) continue;
      const closed = ways.filter(
        ([ox, oy]) => inside(cx + ox, cy + oy) && !linked(cx, cy, cx + ox, cy + oy),
      );
      const d = along(cx, cy);
      const far = (o: readonly [number, number]) => d.get((cy + o[1]) * n + cx + o[0]) ?? 0;
      const nearest = Math.min(...closed.map(far));
      const [ox, oy] = rng.pick(closed.filter((o) => far(o) === nearest));
      open(cx, cy, cx + ox, cy + oy);
    }
  }
  return art;
}

/** Tiles from `from` to `to` along a maze's grass (Infinity: no way). */
function mazeWay(
  art: readonly TileId[],
  w: number,
  from: { x: number; y: number },
  to: { x: number; y: number },
): number {
  const d = new Map<number, number>([[from.y * w + from.x, 0]]);
  const queue = [from.y * w + from.x];
  for (let q = 0; q < queue.length; q++) {
    const i = queue[q]!;
    if (i === to.y * w + to.x) return d.get(i)!;
    const x = i % w;
    for (const j of [i - w, i + w, x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1]) {
      if (j < 0 || j >= art.length || d.has(j) || art[j] === 'mazeHedge') continue;
      d.set(j, d.get(i)! + 1);
      queue.push(j);
    }
  }
  return Infinity;
}

/**
 * A party's round-up by size (docs/PARTY.md): a square paddock (odd, so its middle is a tile),
 * sheep in fours round one in the middle, the sheep in a pen that win at once, the whistle, and
 * hay bales in fours. Inside the Zero-Turn's gas.
 */
export const PARTY_ROUNDUP: Record<
  RaceSize,
  { w: number; sheep: number; target: number; seconds: number; bales: number }
> = {
  quick: { w: 15, sheep: 9, target: 3, seconds: 55, bales: 1 },
  normal: { w: 17, sheep: 13, target: 4, seconds: 62, bales: 2 },
  long: { w: 19, sheep: 17, target: 5, seconds: 70, bales: 2 },
};

/**
 * A party's round-up paddock (docs/PARTY.md), the same seen from every side: a pen in the middle
 * of each player's side (its gate in front; with three players the fourth side has none), a sheep
 * in the middle and the rest in fours turned a quarter round each, hay bales in fours too. Seat 0
 * starts by the bottom pen facing up, the rest the same a quarter turn round each (left, top,
 * right).
 */
export function partyRoundupLawn(seed: string, size: RaceSize, players: number): DuelLawn {
  const rng = Rng.fromSeed(`partyRoundup:${size}:${seed}`);
  const loaner = rng.pick(ROUNDUP_MOWERS);
  const { w, sheep: count, target, seconds, bales } = PARTY_ROUNDUP[size];
  const c = (w - 1) / 2;
  const art = new Array<TileId>(w * w).fill('grass');
  const turns = (p: { x: number; y: number }) => {
    const out = [p];
    for (let k = 1; k < 4; k++) out.push(quarterTurn(w, out[k - 1]!));
    return out;
  };
  const half = (ROUNDUP_PEN.width - 1) / 2;
  // The bottom pen and gate (seat 0's), and its turns for the other seats.
  const basePen: { x: number; y: number }[] = [];
  for (let y = 0; y < ROUNDUP_PEN.depth; y++) {
    for (let x = c - half; x <= c + half; x++) basePen.push({ x, y: w - 1 - y });
  }
  const baseGate = { x: c, y: w - 1 - ROUNDUP_PEN.depth };
  const pens = Array.from({ length: players }, (_, k) => ({
    gate: turns(baseGate)[k]!,
    pen: basePen.map((q) => turns(q)[k]!),
  }));
  for (const { gate, pen } of pens) {
    for (const q of pen) art[q.y * w + q.x] = 'pen';
    art[gate.y * w + gate.x] = 'penGate';
  }
  // Every gate's place (a pen or not), so the paddock stays the same from each side.
  const gates = turns(baseGate);
  const starts = turns({ x: c - 2, y: w - 4 }).map((q, k) => ({
    ...q,
    dir: ([Dir.N, Dir.E, Dir.S, Dir.W] as const)[k]!,
  }));
  const near = (a: { x: number; y: number }, b: { x: number; y: number }, r: number) =>
    Math.abs(a.x - b.x) + Math.abs(a.y - b.y) < r;
  const free = (q: { x: number; y: number }) =>
    q.x >= 1 && q.y >= 1 && q.x <= w - 2 && q.y <= w - 2 && art[q.y * w + q.x] === 'grass';
  const sheep = [{ x: c, y: c }];
  // (Started again from the middle one when the fours do not all fit.)
  for (let tries = 0; sheep.length < count && tries < 6000; tries++) {
    if (tries % 200 === 199) sheep.length = 1;
    const p = { x: rng.intBetween(1, w - 2), y: rng.intBetween(1, c - 1) };
    const four = turns(p);
    const clear = four.every(
      (q) =>
        free(q) &&
        gates.every((g) => !near(q, g, 5)) &&
        starts.every((s) => !near(q, s, 3)) &&
        sheep.every((s) => !near(q, s, 3)) &&
        four.every((o) => o === q || !near(q, o, 3)),
    );
    if (clear) sheep.push(...four);
  }
  let placed = 0;
  for (let tries = 0; placed < bales && tries < 600; tries++) {
    const p = { x: rng.intBetween(2, w - 3), y: rng.intBetween(2, c - 2) };
    const four = turns(p);
    const clear = four.every(
      (q) =>
        [-1, 0, 1].every((ox) => [-1, 0, 1].every((oy) => free({ x: q.x + ox, y: q.y + oy }))) &&
        gates.every((g) => !near(q, g, 4)) &&
        starts.every((s) => !near(q, s, 3)) &&
        sheep.every((s) => !near(q, s, 2)),
    );
    if (!clear) continue;
    for (const q of four) art[q.y * w + q.x] = 'hayBale';
    placed++;
  }
  if (sheep.length < count) throw new Error(`party-roundup-${size}:${seed}: no room for the sheep`);
  const start = starts[0]!;
  const rows = Array.from({ length: w }, (_, y) =>
    art
      .slice(y * w, (y + 1) * w)
      .map((id, x) => (x === start.x && y === start.y ? 'S' : TILE_BY_ID[id].char))
      .join(''),
  );
  const file: LevelFile = {
    id: `party-roundup-${size}`,
    name: 'Sheep Round-Up',
    seed: `partyRoundup:${seed}`,
    biome: 'farm',
    startDir: 'N',
    mowerRule: { kind: 'only', mowers: [loaner] },
    rows,
    pickups: [],
    critters: sheep.map((s) => ({ kind: 'sheep' as const, x: s.x, y: s.y })),
  };
  const level = loadLevel(file);
  const sim = {
    ...level.sim,
    roundup: { pens, target, matchTicks: seconds * 60 },
    seats: starts.slice(0, players),
  };
  return { seed, size, loaner, def: { ...level, sim } };
}

/** Difficulty of the n-th lawn (1-based) in an endless run: ramps up, then holds at the top. */
export function endlessDifficulty(stage: number): number {
  return Math.min(20, 1 + 2 * stage);
}

/**
 * The n-th lawn (1-based) of an Endless Lawns run. Once the run's mower is known, every lawn is
 * built to be beatable with it.
 */
export function endlessLawn(runSeed: string, stage: number, mower?: MowerId): LevelDef {
  const base = paramsForLevel(endlessDifficulty(stage), {
    seed: `endless:${runSeed}:${stage}`,
    id: `endless-${stage}`,
  });
  const { def } = generateLevel(
    mower ? { ...base, mowerRule: { kind: 'only', mowers: [mower] }, required: [mower] } : base,
  );
  return { ...def, name: `Endless ${stage}: ${def.name}` };
}

/** Endless scoring: every blade counts, finishing a lawn is worth a bonus. */
export const ENDLESS_LAWN_BONUS = 50;

/**
 * The lawn an Endless run plays at a stage. A run's lawns are built for its mower, except Endless
 * Lawns' first lawn when the run starts from the menu, which is built before a mower is picked
 * (`builtFor` undefined). Shared with the server, which rebuilds a posted run's lawns this way.
 */
export function endlessRunLawn(
  mode: EndlessMode,
  runSeed: string,
  stage: number,
  builtFor: MowerId | undefined,
): LevelDef {
  if (builtFor) return endlessStage(mode, runSeed, stage, builtFor);
  if (mode !== 'lawns') throw new Error(`endless ${mode}: lawns are built for a mower`);
  return endlessLawn(runSeed, stage);
}

/** Whether a mower may play an Endless mode (the boss runs sit the Fairway Mower out). */
export function endlessModeAllows(mode: EndlessMode, mower: MowerId): boolean {
  return mode === 'lawns' || mode === 'dark' || mode === 'meadow' || bossAllows(mower);
}

/** Rain Chase: rows climbed from the start. */
export function endlessRowsClimbed(sim: Simulation): number {
  return Math.max(0, sim.level.start.y - sim.mower.y);
}

/**
 * Score for one Endless lawn: every blade mowed (yours, against the robot) and a bonus for
 * clearing it; Lawn Art scores the picture; Rain Chase, the rows climbed.
 */
export function endlessStageScore(mode: EndlessMode, sim: Simulation, cleared: boolean): number {
  const mowed = sim.grid.totalGrass - sim.grid.grassRemaining;
  const bonus = cleared ? ENDLESS_LAWN_BONUS : 0;
  switch (mode) {
    case 'rain':
      return endlessRowsClimbed(sim) * 10 + mowed;
    case 'art':
      return Math.round((sim.artMatch() ?? 0) * 100) + bonus;
    case 'robo':
      return (sim.rivalScore()?.you ?? 0) + bonus;
    default:
      return (cleared ? sim.grid.totalGrass : mowed) + bonus;
  }
}

/**
 * Whether an Endless lawn lets the run go on: cleared, and for Lawn Art with the picture up to the
 * stage's mark. Rain Chase is a single lawn: it always ends the run.
 */
export function endlessStagePassed(mode: EndlessMode, stage: number, sim: Simulation): boolean {
  return (
    sim.status === 'won' &&
    mode !== 'rain' &&
    (mode !== 'art' || (sim.artMatch() ?? 0) >= artPassMark(stage))
  );
}

/** What the mower takes into the next Endless lawn: its hearts, and half a tank on top. */
export function endlessCarry(sim: Simulation): CarryOver {
  const cap = sim.mowerSpec.fuelTiles;
  return {
    hp: sim.mower.hp,
    ...(cap !== undefined ? { fuel: Math.min(cap, sim.mower.fuel + Math.ceil(cap / 2)) } : {}),
  };
}

// ── Endless modes (owner decision 2026-10-03) ──────────────────────────────────────────────────

/** The Endless tab's modes: plain lawns, and one escalating run per campaign boss. */
export type EndlessMode =
  'lawns' | 'rain' | 'art' | 'sheep' | 'robo' | 'dark' | 'maze' | 'show' | 'meadow';

export interface EndlessModeDef {
  id: EndlessMode;
  name: string;
  /** One line on the mode's card: at most 26 characters (the card's width; modes.test). */
  blurb: string;
  /** The boss that unlocks it (cleared in the campaign), or null: open from the start. */
  boss: BossKind | null;
}

export const ENDLESS_MODES: readonly EndlessModeDef[] = [
  { id: 'lawns', name: 'Endless Lawns', blurb: 'Lawn after lawn, harder.', boss: null },
  { id: 'rain', name: 'Rain Chase', blurb: 'Stay ahead of the rain.', boss: 'storm' },
  { id: 'art', name: 'Lawn Art', blurb: 'Pictures, judged tougher.', boss: 'art' },
  {
    id: 'sheep',
    name: 'Sheep Round-up',
    blurb: 'More sheep every paddock.',
    boss: 'sheep',
  },
  {
    id: 'robo',
    name: 'Robo Rival',
    blurb: 'A robot that speeds up.',
    boss: 'robo',
  },
  {
    id: 'dark',
    name: 'Lights Out',
    blurb: 'Lawn after lawn, at night.',
    boss: 'dark',
  },
  {
    id: 'maze',
    name: 'Hedge Maze',
    blurb: 'Mazes, guests come sooner.',
    boss: 'maze',
  },
  {
    id: 'show',
    name: 'Mower Soccer',
    blurb: 'More goals, less time.',
    boss: 'show',
  },
  {
    id: 'meadow',
    name: 'Great Meadows',
    blurb: 'Bigger meadow every time.',
    boss: 'meadow',
  },
];

/** Great Meadows runs: each meadow a little bigger, up to the finale's size and past it. */
export function meadowFor(stage: number): { width: number; height: number } {
  return { width: Math.min(28, 16 + 2 * stage), height: Math.min(30, 16 + 2 * stage) };
}

/** Lawn Art runs: the picture match a stage must reach to carry on. */
export function artPassMark(stage: number): number {
  return Math.min(0.92, 0.7 + 0.03 * (stage - 1));
}

/** Sheep Round-up runs: sheep on the n-th paddock. */
export function sheepFor(stage: number): number {
  return Math.min(8, 2 + stage);
}

/** Mower Soccer runs: more goals to score, in shorter halves, round more cones. */
export function soccerFor(stage: number): SoccerOptions {
  return {
    target: Math.min(6, 2 + Math.ceil(stage / 2)),
    slack: Math.max(1.3, 2 - 0.08 * stage),
    cones: Math.min(5, 1 + stage),
  };
}

/** Hedge Maze runs: bigger mazes and less time to spare, maze after maze. */
export function mazeFor(stage: number): { cellsWide: number; cellsTall: number; slack: number } {
  return {
    cellsWide: Math.min(12, 6 + stage),
    cellsTall: Math.min(14, 8 + stage),
    slack: Math.max(1.08, 1.4 - 0.04 * stage),
  };
}

/** Robo Rival runs: the robot's speed (percent of the mower's) on the n-th lawn. */
export function robotSpeedFor(stage: number): number {
  return Math.min(120, 45 + 7 * stage);
}

/**
 * The n-th lawn of an endless run in a mode, built for the run's mower. Rain Chase is a single
 * long lawn (stage 1 only).
 */
export function endlessStage(
  mode: EndlessMode,
  runSeed: string,
  stage: number,
  mower: MowerId,
): LevelDef {
  const seed = `endless:${mode}:${runSeed}:${stage}`;
  const id = `endless-${mode}-${stage}`;
  const named = (def: LevelDef, name: string) => ({ ...def, name });
  switch (mode) {
    case 'lawns':
      return endlessLawn(runSeed, stage, mower);
    case 'rain':
      return named(loadLevel(generateRainChase(seed, id, mower)), 'Rain Chase');
    case 'art':
      // Every picture in the book (the campaign's county fair is always the flower).
      return named(
        loadLevel(
          generateArtLawn(40, seed, id, mower, { ...COUNTY_FAIR, pictures: undefined }).file,
        ),
        `Lawn Art ${stage}`,
      );
    case 'sheep':
      return named(
        loadLevel(generateHerdLawn(60, seed, id, mower, sheepFor(stage)).file),
        `Round-up ${stage}`,
      );
    case 'robo':
      return named(
        loadLevel(generateRoboLawn(80, seed, id, mower, robotSpeedFor(stage)).file),
        `Robo Rival ${stage}`,
      );
    case 'dark':
      return named(
        loadLevel(generateDarkLawn(Math.min(99, 80 + 2 * stage), seed, id, mower).file),
        `Lights Out ${stage}`,
      );
    case 'show':
      return named(
        loadLevel(generateSoccerPitch(140, seed, id, mower, soccerFor(stage)).file),
        `Match ${stage}`,
      );
    case 'maze':
      return named(
        loadLevel(generateMazeLawn(120, seed, id, mower, mazeFor(stage)).file),
        `Hedge Maze ${stage}`,
      );
    case 'meadow':
      return named(
        loadLevel(generateGreatMeadow(160, seed, id, mower, meadowFor(stage)).file),
        `Great Meadow ${stage}`,
      );
  }
}
